import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import {
  paginatedPcmpDisburseVerifyQueueSchema,
  pcmpDisburseVerifyCountsSchema,
  pcmpDisburseVerifyResultSchema,
  type PaginatedPcmpDisburseVerifyQueue,
  type PcmpDisburseVerifyCounts,
  type PcmpDisburseVerifyPayload,
  type PcmpDisburseVerifyQueueParams,
  type PcmpDisburseVerifyResult,
} from '../types/pcmpDisburseVerify.types';
import type { PcmpQueueFilters } from '../types/pcmpQueue.types';

export const pcmpDisburseVerifyQueryKeys = {
  all: () => ['pcmp-disburse-verify'] as const,
  queue: (params: PcmpDisburseVerifyQueueParams) =>
    [...pcmpDisburseVerifyQueryKeys.all(), 'queue', params] as const,
  counts: (filters: PcmpQueueFilters) =>
    [...pcmpDisburseVerifyQueryKeys.all(), 'counts', filters] as const,
};

export const pcmpDisburseVerifyQueueQueryOptions = (params: PcmpDisburseVerifyQueueParams) =>
  queryOptions({
    queryKey: pcmpDisburseVerifyQueryKeys.queue(params),
    queryFn: async (): Promise<PaginatedPcmpDisburseVerifyQueue> => {
      const res = await apiClient.get(API.pcmpDisburseVerify.queue, { params });
      return paginatedPcmpDisburseVerifyQueueSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

export const pcmpDisburseVerifyCountsQueryOptions = (filters: PcmpQueueFilters) =>
  queryOptions({
    queryKey: pcmpDisburseVerifyQueryKeys.counts(filters),
    queryFn: async (): Promise<PcmpDisburseVerifyCounts> => {
      const res = await apiClient.get(API.pcmpDisburseVerify.queueCounts, { params: filters });
      return pcmpDisburseVerifyCountsSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

/** Final; proposals verified meanwhile or no longer disbursed come back as skipped. */
export async function verifyPcmpDisbursements(
  payload: PcmpDisburseVerifyPayload,
): Promise<PcmpDisburseVerifyResult> {
  const res = await apiClient.post(API.pcmpDisburseVerify.verify, payload);
  return pcmpDisburseVerifyResultSchema.parse(res.data);
}
